import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { isInElectron } from '@helpers/engine/electron';
import {
  liveVersion,
  localVersion,
  versionInfoToSemver,
} from '@helpers/engine/version';
import { LoggerService } from '@services/logger.service';
import { marked, Renderer } from 'marked';
import { interval } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private logger = inject(LoggerService);

  public versionString = computed(() => {
    const local = localVersion();
    if (!local) return '';

    return versionInfoToSemver(local);
  });

  public liveVersionString = computed(() => {
    const live = liveVersion();
    if (!live) return '';

    return versionInfoToSemver(live);
  });

  public versionMismatch = computed(
    () =>
      !!this.liveVersionString() &&
      this.versionString() !== this.liveVersionString(),
  );

  public changelogCurrent = signal<string>('');
  public changelogAll = signal<string>('');

  public hasChangelogs = computed(
    () => !!this.changelogAll() && !!this.changelogCurrent(),
  );

  private localVersionLoad?: Promise<void>;

  private get renderer() {
    const renderer = new Renderer();
    renderer.heading = ({ tokens, depth }) => {
      const text = renderer.parser.parseInline(tokens);

      const sizeClass = [1, 2].includes(depth) ? 'text-xl' : 'text-lg';

      return `
          <h${depth} class="${sizeClass}">
            ${text}
          </h${depth}>`;
    };

    return renderer;
  }

  async init() {
    await this.loadLocalVersion();

    try {
      const changelog = await fetch('CHANGELOG.md');
      const changelogData = await changelog.text();
      this.changelogAll.set(
        await marked(changelogData, { renderer: this.renderer }),
      );
    } catch {
      this.logger.error(
        'Meta:Changelog',
        'Could not load changelog (all) - probably on local.',
      );
    }

    try {
      const changelog = await fetch('CHANGELOG-current.md');
      const changelogData = await changelog.text();
      this.changelogCurrent.set(
        await marked(changelogData, { renderer: this.renderer }),
      );
    } catch {
      this.logger.error(
        'Meta:Changelog',
        'Could not load changelog (current) - probably on local.',
      );
    }

    this.checkVersionAgainstLiveVersion();

    interval(15 * 60 * 1000).subscribe(() => {
      this.checkVersionAgainstLiveVersion();
    });
  }

  // Memoized so content loading can wait on the same fetch that init starts.
  public loadLocalVersion(): Promise<void> {
    this.localVersionLoad ??= this.fetchLocalVersion();
    return this.localVersionLoad;
  }

  private async fetchLocalVersion() {
    this.logger.log('Meta:Version', 'Checking for local version...');

    try {
      const response = await fetch('version.json');
      const versionInfo = await response.json();
      localVersion.set(versionInfo);

      this.logger.log(
        'Meta:Version',
        `Got local version: ${this.versionString()}`,
      );
    } catch (e) {
      this.logger.error('Meta:Version', 'Failed to load version info', e);
    }
  }

  private async checkVersionAgainstLiveVersion() {
    if (!environment.production) return;

    this.logger.log('Meta:Version', 'Checking for live version...');

    try {
      const liveVersionFile = await fetch(
        'https://reformkingdom.felfhenor.com/version.json',
      );
      const liveVersionData = await liveVersionFile.json();
      liveVersion.set(liveVersionData);

      this.logger.log(
        'Meta:Version',
        `Got live version: ${this.liveVersionString()}`,
      );
    } catch {
      this.logger.error(
        'Meta:Version',
        'Could not load live version data. Probably not a big deal.',
      );
    }
  }

  public update() {
    if (!isInElectron()) {
      window.location.reload();
      return;
    }

    window.open('https://seiyria.itch.io/reform-kingdom');
  }

  // The desktop shell saves before letting the window close.
  public quit() {
    window.close();
  }
}
